"use client";

import {
  sanitizeGrowthDevice,
  sanitizeGrowthSessionId,
  type GrowthDevice,
  type GrowthFunnelStep,
  type GrowthPlan,
  type GrowthTier,
} from "./growth-funnel-shared";

const GROWTH_SESSION_STORAGE_KEY = "carpybara:growth-session:v1";

export type GrowthSessionContext = {
  growthSessionId: string;
  growthDevice: GrowthDevice;
};

function createGrowthSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
}

function currentDevice(): GrowthDevice {
  return window.matchMedia("(max-width: 767px)").matches
    ? "mobile"
    : "desktop";
}

export function getGrowthSessionContext(): GrowthSessionContext {
  if (typeof window === "undefined") {
    return {
      growthSessionId: "server_render_unavailable",
      growthDevice: "desktop",
    };
  }

  let growthSessionId: string | null = null;
  try {
    growthSessionId = sanitizeGrowthSessionId(
      window.sessionStorage.getItem(GROWTH_SESSION_STORAGE_KEY),
    );
  } catch {
    // Storage can be unavailable in strict privacy modes. The in-memory id is
    // still valid for the current navigation and Stripe handoff.
  }

  if (!growthSessionId) {
    growthSessionId = createGrowthSessionId();
    try {
      window.sessionStorage.setItem(
        GROWTH_SESSION_STORAGE_KEY,
        growthSessionId,
      );
    } catch {
      // See the storage note above.
    }
  }

  return { growthSessionId, growthDevice: currentDevice() };
}

type ClientGrowthFunnelProperties = {
  plan?: GrowthPlan;
  tier?: GrowthTier;
  source?: string;
  surface?: string;
};

export function recordClientGrowthFunnelStep(
  step: Extract<GrowthFunnelStep, "pricing_viewed" | "upgrade_clicked">,
  properties: ClientGrowthFunnelProperties = {},
): GrowthSessionContext {
  const context = getGrowthSessionContext();
  const body = JSON.stringify({ step, ...context, ...properties });

  try {
    if (typeof navigator.sendBeacon === "function") {
      const queued = navigator.sendBeacon(
        "/api/growth/funnel",
        new Blob([body], { type: "application/json" }),
      );
      if (queued) return context;
    }
  } catch {
    // Fall through to keepalive fetch when Beacon is unavailable or blocked.
  }

  void fetch("/api/growth/funnel", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    credentials: "same-origin",
  }).catch(() => undefined);

  return context;
}

export function parseGrowthContext(input: unknown): GrowthSessionContext | null {
  if (!input || typeof input !== "object") return null;
  const value = input as Record<string, unknown>;
  const growthSessionId = sanitizeGrowthSessionId(value.growthSessionId);
  const growthDevice = sanitizeGrowthDevice(value.growthDevice);
  return growthSessionId && growthDevice
    ? { growthSessionId, growthDevice }
    : null;
}
